import React, { useState, useEffect , useRef,useMemo} from 'react';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// Success Icon Component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = '/api';

const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center  ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  )};

const SiteIndentFormModal = ({ open, onClose, initialData, onSubmit , onFormSuccess}) => {
  
  // --- STATE ---
  const initialHeader = {
    SiteIndentID: null,
    SiteIndentNo: '',
    Date: new Date().toISOString().split('T')[0],
    VendorCode: '', 
    SurveyBOQID: '', 
    TransferType: 'Transfer to Contractor', 
    Status: 'Open',
    MaterialStatus: 'Pending',
    ProjectHeaderId: '',
    ProjectNo: '',
    FromFeederId: '',
    ToFeederId: '', 
    Circle: '',
    District: '',
    Division: '',
    SubDivision: '',
    SubStation: '',
    FeederEngineerName: '',
    Remarks: '',
    BcSuccess: false,
    ActivityId: null,
    SubActivityId: null
  };

  const initialLine = {
    SiteIndentLineID: null,
    SurveyBOQLineID: null, 
    ProductCode: '',
    FeederId: '',
    TaskNo: '',
    Description: '',
    Remark: '',
    UnitOfMeasure: 'NOS',
    AvailableStock: 0,
    StockOnAvailableIndent: 0,
    Quantity: 0,
    QtyToIssue: 0,
    QuantityIssued: 0,
    QtyToReceive: 0,
    QuantityReceived: 0,
    ItemCategoryCode: '',
    LineNumber: 100,
    OriginalQtyToIssue: 0, // Stores original DB value
    RemainingQty: 0 ,
    TotalIndentedQty: 0,
    BcSuccess: false,
     PendingDPRQty: 0,
     ActivityId: null,
    SubActivityId: null
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  // ADD THIS STATE:
  const [highlightedRows, setHighlightedRows] = useState([]);
  const [projectActivities, setProjectActivities] = useState([]);

    // --- TOAST / ALERT STATE ---
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // Dropdown Data
  const [projects, setProjects] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [availableBOQs, setAvailableBOQs] = useState([]); 
  const [nextIndentNumber, setNextIndentNumber] = useState('');
   const [subActivities, setSubActivities] = useState([]);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [isManualProjectChange, setIsManualProjectChange] = useState(false);
  const [isLoadingStockData, setIsLoadingStockData] = useState(false);
  const [pendingStockFetch, setPendingStockFetch] = useState(null);
  const isHeaderLocked = header.BcSuccess === true || header.BcSuccess === 1;


    // --- NEW STATE FOR POPUP ---
  const [showSelectionPopup, setShowSelectionPopup] = useState(false);
  const [popupData, setPopupData] = useState([]); // Stores the full list fetched from API
  const [selectedPopupIds, setSelectedPopupIds] = useState(new Set()); // Stores IDs of checked items
  const [popupSearchTerm, setPopupSearchTerm] = useState('');
  const [popupHighlightedIndex, setPopupHighlightedIndex] = useState(-1);
  const popupListRef = useRef(null);

  // 🔹 COLUMN RESIZE ADDITION
const [columnWidths, setColumnWidths] = useState({
  lineNo: 80,
  productCode: 160,
  taskNo: 120,
  feeder: 120,
  productName: 280,
   activity: 200,
  subActivity: 200,
  availStock: 200,
  stockIndent: 200,
  qty: 200,
  toIssue: 200,
  issued: 200,
  qtyToRecv: 200,
  qtyRecv: 200,
  uom: 80,
  action: 80,
   remark: 200
});

const resizingRef = useRef(null);


  // --- EFFECTS ---
  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      resetForm();
      fetchProjects();
      fetchVendors();
      fetchFeeders();

      // DETERMINE MODE BASED ON ID, NOT JUST DATA EXISTENCE
      const isEditMode = initialData && initialData.header && initialData.header.SiteIndentID;
      
      // If it is NOT edit mode, fetch the next number
      // (This ensures "Create with Pre-filled Data" gets a new number)
      if (!isEditMode) {
        fetchNextIndentNumber();
      }

      if (initialData) {
        // --- APPLY INITIAL DATA (Used for BOTH Edit Mode AND Pre-filled Create Mode) ---
        const sourceData = initialData.header ? initialData.header : initialData;
        
        setHeader(prev => ({
          ...initialHeader,
          ...sourceData,
          Date: sourceData.Date ? sourceData.Date.split('T')[0] : new Date().toISOString().split('T')[0],
          ProjectHeaderId: sourceData.ProjectHeaderId || '',
          FromFeederId: sourceData.FromFeederId || '',
          ToFeederId: sourceData.ToFeederId || ''
        }));

        let editLines = [];
        if (sourceData.Lines) editLines = sourceData.Lines;
        else if (initialData.lines) editLines = initialData.lines;

        if (editLines.length > 0) {
            // UPDATED: Fetch stats for each line to get remaining qty
            const processedLinesPromises = editLines.map(async (line) => {
                const excludeId = line.SiteIndentLineID || 0;
                let remaining = 0;
                 let totalIndented = 0;

                try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_URL}/survey-boq-line/${line.SurveyBOQLineID}/indent-stats?excludeSiteIndentLineId=${excludeId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if(data.success) {
                      remaining = data.data.RemainingForIndent || 0;
                        totalIndented = data.data.TotalIndentedQty || 0;
                      }
                } catch (e) {
                    // console.error("Error fetching remaining qty for line", e);
                    
                }

                return {
                    ...line,
                    // Use the calculated sum from API if available, otherwise fallback to existing value
                    QuantityIssued: line.TotalIssued !== undefined ? line.TotalIssued : (line.QuantityIssued || 0),
                    QuantityReceived: line.TotalReceived !== undefined ? line.TotalReceived : (line.QuantityReceived || 0),
                    // Populate new state fields
                    OriginalQtyToIssue: parseFloat(line.QtyToIssue) || 0,
                    RemainingQty: remaining,
                    TotalIndentedQty: totalIndented
                };
            });

            Promise.all(processedLinesPromises).then(updatedLines => {
  const finalLines = updatedLines.map(l => ({
    ...l,
    FeederId: sourceData.FromFeederId || l.FeederId
  }));
  
  setLines(finalLines);

  if (sourceData.ProjectHeaderId && sourceData.ToFeederId && sourceData.VendorCode) {
        fetchPendingDprBulk(sourceData.ProjectHeaderId, sourceData.ToFeederId, sourceData.VendorCode, finalLines);
    }
  // 🔹 FETCH STOCK DATA IN EDIT MODE

// Condition: We have FromFeederId, ProjectNo, and lines with ProductCodes
const hasPreFilledData = sourceData.FromFeederId && sourceData.ProjectNo && finalLines.length > 0;
const hasProductCodes = finalLines.some(l => l.ProductCode);

if (hasPreFilledData && hasProductCodes) {
  console.log('🔄 Setting pending stock fetch...', {
    Mode: isEditMode ? 'Edit' : 'Create from Survey',
    FromFeederId: sourceData.FromFeederId,
    ProjectNo: sourceData.ProjectNo,
    LinesCount: finalLines.length,
    ProductCodes: finalLines.map(l => l.ProductCode)
  });
  
  // Store the data needed for stock fetch - useEffect will handle it when feeders load
  setPendingStockFetch({
    fromFeederId: sourceData.FromFeederId,
    projectNo: sourceData.ProjectNo,
    lines: finalLines
  });
}
});
        } else {
            setLines([initialLine]);
        }
      }
    }
  }, [open, initialData]);

  // --- EFFECT: SYNC LINE FEEDERS WITH HEADER FROM FEEDER ---
  useEffect(() => {
    if (header.FromFeederId) {
      setLines(prevLines => prevLines.map(line => ({
        ...line,
        FeederId: header.FromFeederId
      })));
    }
  }, [header.FromFeederId]);

  // --- EFFECT: FETCH BOQS WHEN PROJECT + FEEDER ARE SELECTED ---
  useEffect(() => {
    const fetchBOQs = async () => {
      if (header.ProjectNo && header.ToFeederId) {
        const selectedFeeder = feeders.find(f => f.FeederId == header.ToFeederId);
        if (selectedFeeder && selectedFeeder.Code) {
          try {
            const token = localStorage.getItem('authToken');
            const feederCode = encodeURIComponent(selectedFeeder.Code);
            // In your React component's fetchBOQs function
            const currentId = (initialData && initialData.header && initialData.header.SiteIndentID) ? initialData.header.SiteIndentID : null;

            const res = await fetch(`${API_URL}/survey-boq/by-project-feeder?projectNo=${header.ProjectNo}&feederCode=${feederCode}&currentSiteIndentId=${currentId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!res.ok) {
                // console.error("API Error fetching BOQs");
                setAvailableBOQs([]); 
                return;
            }

            const data = await res.json();
            
            if (Array.isArray(data)) {
              setAvailableBOQs(data);
            } else {
              // console.warn("BOQ API returned non-array data:", data);
              setAvailableBOQs([]);
            }

          } catch (e) {
            // console.error('Error fetching BOQs', e);
            setAvailableBOQs([]); 
          }
        }
      } else {
        setAvailableBOQs([]);
      }
    };
    
    if (feeders.length > 0) {
      fetchBOQs();
    }
  }, [header.ProjectNo, header.ToFeederId, feeders]);

  // --- UPDATED EFFECT FOR SUB-ACTIVITIES ---
  useEffect(() => {
    // 1. If no parent is selected, clear sub-activities
    if (!header.ActivityId) {
      setSubActivities([]);
      // Only clear header ID if we are in Create mode
      setHeader(prev => {
        if (prev.SiteIndentID) return prev; // Keep ID if Editing
        if (prev.SubActivityId == null) return prev;
        return { ...prev, SubActivityId: null };
      });
      return;
    }

    // 2. If the master list hasn't loaded yet, do nothing
    if (projectActivities.length === 0) {
      return;
    }

    // 3. Find the selected parent object (Robust Number Comparison)
    const selectedParent = projectActivities.find(
      a => Number(a.ActivityId) === Number(header.ActivityId)
    );

    if (selectedParent) {
      // 4. Filter children (Robust String & ID Comparison)
      const parentCode = String(selectedParent.Code || '').trim().toUpperCase();
      const parentId = Number(selectedParent.ActivityId);
      
      const children = projectActivities.filter(a => {
        // Match by Code (Case-insensitive)
        const byCode = String(a.ParentActivityCode || '').trim().toUpperCase() === parentCode;
        // Match by ID (Handles multiple possible ID column names)
        const byParentId =
          Number(a.ParentActivityId) === parentId ||
          Number(a.ParentId) === parentId ||
          Number(a.ParentActivityID) === parentId;
          
        return byCode || byParentId;
      });

      setSubActivities(children);

      // 5. VALIDATION LOGIC
      const isCurrentSubActivityValid = children.some(
        c => Number(c.ActivityId) === Number(header.SubActivityId)
      );

      // 6. ONLY CLEAR THE ID IF WE ARE IN CREATE MODE
      const isEditMode = !!header.SiteIndentID;

      if (
        projectActivities.length > 0 &&
        !isCurrentSubActivityValid &&
        !isEditMode &&
        header.SubActivityId !== null
      ) {
        setHeader(prev => ({ ...prev, SubActivityId: null }));
      }
    } else {
      // Parent not found in the list
      setSubActivities([]);
      // Keep ID in Edit mode, clear in Create mode
      setHeader(prev => {
        if (prev.SiteIndentID) return prev;
        if (prev.SubActivityId == null) return prev;
        return { ...prev, SubActivityId: null };
      });
    }
  }, [header.ActivityId, projectActivities]);


useEffect(() => {
  if (pendingStockFetch && feeders.length > 0) {
    console.log('✅ Feeders loaded! Now fetching stock data...', {
      fromFeederId: pendingStockFetch.fromFeederId,
      projectNo: pendingStockFetch.projectNo,
      feedersCount: feeders.length
    });
    
    fetchStockDataFromBC(
      pendingStockFetch.fromFeederId, 
      pendingStockFetch.lines, 
      pendingStockFetch.projectNo,
      feeders  // 🔹 PASS feeders as parameter
    );
    
    // Clear the pending flag
    setPendingStockFetch(null);
  }
}, [feeders, pendingStockFetch]);

  // --- API HANDLERS ---
  const fetchNextIndentNumber = async () => {
    try {
      const token = localStorage.getItem('authToken')
      const res = await fetch(`${API_URL}/site-indent/next-number`,{headers: { 'Authorization': `Bearer ${token}` }});
      const data = await res.json();
      if (data && data.nextSiteIndentNo) setNextIndentNumber(data.nextSiteIndentNo);
    } catch (e) {  setNextIndentNumber('SI-0001'); }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {  setProjects([]); }
  };

    const fetchActivities = async (projectNo) => {
    if (!projectNo) {
      setProjectActivities([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      const data = await res.json();
      setProjectActivities(data || []);
    } catch (e) {
      console.error("Error fetching activities:", e);
      setProjectActivities([]);
    }
  };

    // ADD THIS NEW EFFECT: Fetch activities when Project changes in Header
  useEffect(() => {
    if (header.ProjectNo) {
      fetchActivities(header.ProjectNo);
    } else {
      setProjectActivities([]);
    }
  }, [header.ProjectNo]);

  // --- UPDATE THIS FUNCTION ---
const fetchVendors = async (feederCode = null, projectNo = null) => {
  try {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}/vendors`;
    
    // If a feeder is selected, use the new specific API
    if (feederCode) {
      const params = new URLSearchParams({ feederCode });
      if (projectNo) params.append('projectNo', projectNo);
      url = `${API_URL}/vendors-by-feeder?${params.toString()}`;
    }

    const res = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    setVendors(Array.isArray(data) ? data : []);
  } catch (e) {  
    console.error(e);
    setVendors([]); 
  }
};

  const fetchFeeders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/feeder-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFeeders(Array.isArray(data) ? data : []);
    } catch (e) { setFeeders([]); }
  };

  // --- EVENT HANDLERS ---
  // Fetch stock data from BC - sends all items in one API call
// --- EVENT HANDLERS ---
// Fetch stock data from BC - sends all items in one API call
const fetchStockDataFromBC = async (fromFeederId, currentLines = null, projectNo = null, feedersList = null) => {
  const linesToUse = currentLines || lines;
  const effectiveProjectNo = projectNo || header.ProjectNo;
  const effectiveFeeders = feedersList || feeders; // Use passed feeders or fall back to state
  
  // ... (keep existing console logs if needed) ...

  if (!effectiveProjectNo || !fromFeederId || linesToUse.length === 0) {
    console.log('❌ Early return - missing data');
    return;
  }

  const fromFeeder = effectiveFeeders.find(f => f.FeederId == fromFeederId);

  if (!fromFeeder || !fromFeeder.Code) {
    console.log("❌ From feeder code not found - RETURNING EARLY");
    return;
  }

  // Prepare items array
  const items = linesToUse
    .map((line, index) => ({
      Item_No: line.ProductCode,
      lineIndex: index
    }))
    .filter(item => item.Item_No);

  if (items.length === 0) {
    console.log("No items to fetch stock data for");
    return;
  }

  setIsLoadingStockData(true);

  try {
    const token = localStorage.getItem('authToken');
    
    // Single API call with all items
    const response = await fetch(`${API_URL}/mapping/site-indent/fetch-item-stock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
         Project_No: effectiveProjectNo,
        From_Site: fromFeeder.Code,
        items: items
      })
    });

    const data = await response.json();
    
    if (data.success && data.data.items) {
      
      // ✅ FIX: Create a map for easy lookup by ProductCode
      const stockMap = new Map();
      data.data.items.forEach(item => {
        stockMap.set(item.Item_No, item);
      });

      // ✅ FIX: Use functional state update to merge data
      // This ensures we don't overwrite PendingDPRQty that was fetched by fetchPendingDprBulk
      setLines(prevLines => {
        return prevLines.map(line => {
          const stockItem = stockMap.get(line.ProductCode);
          
          // If we found stock data for this product, merge it
          if (stockItem) {
            return {
              ...line, // Keep existing line data (including the NEW PendingDPRQty)
              AvailableStock: stockItem.AvailableStock,
              StockOnAvailableIndent: stockItem.StockOnAvailableIndent
            };
          }
          // Otherwise return line as is
          return line;
        });
      });

      showToast(`Stock data updated for ${data.data.items.length} items`, 'success', 'Stock Data Loaded');
    }
  } catch (error) {
    console.error("Error fetching stock data:", error);
    showToast('Failed to fetch stock data from BC', 'error', 'API Error');
  } finally {
    setIsLoadingStockData(false);
  }
};
  
  const handleHeaderChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
    if (['SurveyBOQID', 'ActivityId', 'SubActivityId'].includes(field)) {
      setLines([initialLine]); // Resets table to one empty row
    }
    if (field === 'VendorCode') {
        // If vendor changes, re-calculate pending DPR
        if (header.ProjectHeaderId && header.ToFeederId && lines.length > 0) {
            fetchPendingDprBulk(header.ProjectHeaderId, header.ToFeederId, value, lines);
        }
    }
  };

  const handleToFeederChange = (e) => {
    const feederId = e.target.value;
    setHeader(prev => ({ ...prev, ToFeederId: feederId }));

    const selectedFeeder = feeders.find(f => f.FeederId == feederId);
    if (selectedFeeder && selectedFeeder.Code) {
    // Call the updated fetchVendors with the Feeder Code and Project No
    fetchVendors(selectedFeeder.Code, header.ProjectNo);
    
    // 3. Reset the Vendor Code because the previous vendor might not apply to this new feeder
    setHeader(prev => ({ ...prev, VendorCode: '' }));
    
    // 4. Recalculate pending DPR if needed (your existing logic)
    if (header.ProjectHeaderId && lines.length > 0) {
      // Note: VendorCode is empty here, so this might need adjustment depending on logic
      // fetchPendingDprBulk(...); 
    }
  } else {
    // If no feeder selected, clear vendors
    setVendors([]);
  }

     if (header.ProjectHeaderId && header.VendorCode && lines.length > 0) {
        fetchPendingDprBulk(header.ProjectHeaderId, feederId, header.VendorCode, lines);
    }

    if (selectedFeeder) {
      setHeader(prev => ({
        ...prev,
        Circle: selectedFeeder.Circle || prev.Circle,
        District: selectedFeeder.District || prev.District,
        Division: selectedFeeder.Division || prev.Division,
        SubDivision: selectedFeeder.SubDivision || prev.SubDivision,
        SubStation: selectedFeeder.SubStation || prev.SubStation,
        FeederEngineerName: selectedFeeder.EngineerName || prev.FeederEngineerName
      }));
    } else {
      setHeader(prev => ({
        ...prev,
        Circle: '',
        District: '',
        Division: '',
        SubDivision: '',
        SubStation: ''
      }));
    }
  };

  const handleProjectChange = (e) => {
    const projectNo = e.target.value;
    const projectObj = projects.find(p => p.No === projectNo);
    
    setSelectedProject(projectObj);
    setIsManualProjectChange(true);
    setLines([initialLine]); 
    setVendors([])
    setHeader(prev => ({
      ...prev,
      ProjectHeaderId: projectObj ? projectObj.ProjectHeaderId : '',
      ProjectNo: projectNo,
      ToFeederId: '',
      FromFeederId: '',
      VendorCode: '',
      Circle: projectObj?.Circle || '',
      District: projectObj?.District || ''
    }));
  };

 

  // UPDATED: Fetches stats for Create Mode
    const handleBOQSelection = async (e) => {
    const boqId = e.target.value;
    
    // Only update the header and reset lines. DO NOT fetch lines yet.
    setHeader(prev => ({ ...prev, SurveyBOQID: boqId }));
    setLines([initialLine]);
  };


  const addLine = () => {
    const lastLine = lines[lines.length - 1];
    const nextLineNumber = lastLine ? parseInt(lastLine.LineNumber) + 100 : 100;
    setLines([...lines, { 
        ...initialLine, 
        LineNumber: nextLineNumber,
        FeederId: header.FromFeederId || null // Ensure new line gets current FromFeeder
    }]);
  }
  
  const removeLine = (index) => {
    if (lines.length > 1) setLines(lines.filter((_, i) => i !== index));
  };

  const handleLineChange = (index, field, value) => {
    // --- UPDATED LOGIC FOR QtyToIssue ---
    if (field === 'QtyToIssue') {
      const line = lines[index];
      const originalQty = parseFloat(line.OriginalQtyToIssue) || 0;
      const remainingQty = parseFloat(line.RemainingQty) || 0;
      const totalIndentedQty = parseFloat(line.TotalIndentedQty) || 0;
      let newVal = parseFloat(value);

      if (isNaN(newVal)) newVal = 0;

      const difference = newVal ;
      
      if (difference > remainingQty) {
        const maxAllowed =  remainingQty;

        // ✅ FIND THE TO FEEDER NAME
        const toFeeder = feeders.find(f => f.FeederId == header.ToFeederId);
        const toFeederName = toFeeder 
          ? `${toFeeder.Code}` 
          : 'N/A';

        // alert(`Cannot exceed remaining quantity. You can only increase up to ${maxAllowed.toFixed(6)} (Original: ${originalQty} + Remaining: ${remainingQty}).`);
        showToast(
          `Already consumed:  ${totalIndentedQty.toFixed(6)}\nBal Qty:  ${remainingQty.toFixed(6)}\nFor selected:  ${toFeederName}`, 
          'error', 
          'Validation Error'
        );
        newVal = maxAllowed;
      }
      
      if (newVal < 0) newVal = 0;

      const newLines = [...lines];
      newLines[index][field] = newVal;
      setLines(newLines);
      return;
    }

    // --- DEFAULT LOGIC ---
    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const resetForm = () => {
    setHeader(initialHeader);
    setLines([initialLine]);
    setNextIndentNumber('');
    setIsManualProjectChange(false);
    setSelectedProject(null);
    setAvailableBOQs([]);
    
  };

  // 🔹 COLUMN RESIZE ADDITION
const startResize = (columnKey, startX) => {
  resizingRef.current = {
    columnKey,
    startX,
    startWidth: columnWidths[columnKey]
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', stopResize);
};

const handleMouseMove = (e) => {
  if (!resizingRef.current) return;

  const { columnKey, startX, startWidth } = resizingRef.current;
  const delta = e.clientX - startX;

  setColumnWidths(prev => ({
    ...prev,
    [columnKey]: Math.max(60, startWidth + delta)
  }));
};

const stopResize = () => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopResize);
  resizingRef.current = null;
};


// Function to fetch pending DPR qty in BULK
const fetchPendingDprBulk = async (projectHeaderId, feederId, contractorCode, currentLines) => {
  if (!projectHeaderId || !feederId || !contractorCode) return;
  
  // Get unique product codes from lines
  const productCodes = currentLines
    .map(l => l.ProductCode)
    .filter(code => code); // Remove empty

  if (productCodes.length === 0) return;

  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/site-indent/pending-dpr-bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        projectId: projectHeaderId,
        feederId: feederId, // ToFeederId (Target Location)
        contractorCode: contractorCode,
        productCodes: productCodes
      })
    });

    const data = await response.json();
    
    // Update lines with fetched data
    setLines(prevLines => prevLines.map(line => ({
      ...line,
      PendingDPRQty: data[line.ProductCode] !== undefined ? data[line.ProductCode] : (line.PendingDPRQty || 0)
    })));

  } catch (error) {
    console.error("Error fetching bulk pending DPR", error);
  }
};

  // --- ADD THIS NEW HANDLER ---
   // --- ADD THIS HANDLER ---
   // --- UPDATED HANDLER ---
    const handleGetFilteredLines = async () => {
    if (!header.SurveyBOQID) {
      showToast('Please select a BOQ first.', 'error', 'Validation');
      return;
    }
    if (!header.ActivityId) {
      showToast('Please select an Activity.', 'error', 'Validation');
      return;
    }
    if (!header.SubActivityId) {
      showToast('Please select a Sub-Activity.', 'error', 'Validation');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${API_URL}/survey-boq-indent-line/${header.SurveyBOQID}?activityId=${header.ActivityId}&subActivityId=${header.SubActivityId}`, 
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error('Failed to fetch BOQ lines');
      const boqData = await res.json();
      
      if (boqData.lines && Array.isArray(boqData.lines) && boqData.lines.length > 0) {
        const linesWithStatsPromises = boqData.lines.map(async (boqLine, index) => {
            let remainingQty = 0;
            let totalIndentedQty = 0;
            try {
                const statsRes = await fetch(`${API_URL}/survey-boq-line/${boqLine.LineId}/indent-stats`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const statsData = await statsRes.json();
                if(statsData.success) {
                    remainingQty = statsData.data.RemainingForIndent || 0;
                    totalIndentedQty = statsData.data.TotalIndentedQty || 0;
                }
            } catch (err) { console.error("Stats error", err); }

            return {
                ...initialLine,
                SurveyBOQLineID: boqLine.LineId,
                ProductCode: boqLine.ItemNo, 
                Description: boqLine.ItemDescription || boqLine.ItemNo, 
                UnitOfMeasure: boqLine.UOM,
                Quantity: boqLine.SurveyQuantity || 0, 
                QtyToIssue: boqLine.QtyToIndent || 0,
                TaskNo: boqLine.JobTaskNo || '', // Ensure this is mapped
                FeederId: header.FromFeederId || null, 
                LineNumber: 10000 + (index * 10000),
                OriginalQtyToIssue: parseFloat(boqLine.QtyToIndent) || 0,
                RemainingQty: remainingQty,
                TotalIndentedQty: totalIndentedQty,
                Remark: boqLine.Remark || '',
                ActivityId: header.ActivityId, // Attach context
                SubActivityId: header.SubActivityId // Attach context
            };
        });

        const newLines = await Promise.all(linesWithStatsPromises);
        
        setPopupData(newLines);
        
        // 🔹 NEW: Pre-select items that are already in the main table
        const currentLineIds = new Set(lines.map(l => l.SurveyBOQLineID).filter(Boolean));
        setSelectedPopupIds(currentLineIds);
        
        setPopupSearchTerm('');
        setPopupHighlightedIndex(-1);
        setShowSelectionPopup(true);

      } else {
        setLines([initialLine]);
        showToast('No matching lines found.', 'warning', 'No Data');
      }
    } catch (error) {
      console.error(error);
      showToast('Failed to load lines.', 'error', 'Error');
    }
  };

    // --- HELPERS FOR POPUP HEADER ---
  // --- HELPERS FOR POPUP HEADER ---
  const getPopupHeaderInfo = () => {
    // 1. BOQ Info - Get Code
    const boqObj = availableBOQs.find(b => b.SurveyBOQID == header.SurveyBOQID);
    const boqDisplay = boqObj ? boqObj.SurveyBOQNo : 'N/A';

    // 2. Activity Info - Get Code
    const actObj = projectActivities.find(a => a.ActivityId == header.ActivityId);
    const activityDisplay = actObj ? actObj.Code : 'N/A';

    // 3. Sub-Activity Info - Get Code
    const subActObj = subActivities.find(a => a.ActivityId == header.SubActivityId);
    const subActivityDisplay = subActObj ? subActObj.Code : 'N/A';

    return { boqDisplay, activityDisplay, subActivityDisplay };
  };

  const { boqDisplay, activityDisplay, subActivityDisplay } = getPopupHeaderInfo();


    // --- POPUP HANDLERS ---
   const handlePopupLoad = () => {
    const selectedIds = selectedPopupIds;
    
    if (selectedIds.size === 0) {
      showToast('Please select at least one item.', 'warning', 'Selection Required');
      return;
    }

    // 1. Separate lines: "Keep" vs "New"
    // Keep the objects from the current state to preserve any edits (e.g. Qty changes)
    const linesToKeep = lines.filter(l => selectedIds.has(l.SurveyBOQLineID));

    // 2. Identify items that are in the selection but NOT in the current table
    const currentTableIds = new Set(lines.map(l => l.SurveyBOQLineID).filter(Boolean));
    const trulyNewItems = popupData.filter(item => 
      selectedIds.has(item.SurveyBOQLineID) && !currentTableIds.has(item.SurveyBOQLineID)
    );

    // 3. Handle Line Numbers for new items
    // Find the highest Line Number currently in the table to continue the sequence
    const maxLineNumber = linesToKeep.length > 0 
      ? Math.max(...linesToKeep.map(l => parseInt(l.LineNumber) || 0)) 
      : 0;

    const itemsWithCorrectLineNos = trulyNewItems.map((item, index) => ({
      ...item,
      LineNumber: maxLineNumber + 10000 + (index * 10000) // Increment by 100
    }));

    // 4. Combine: [Existing Kept Lines] + [New Appended Lines]
    const finalLines = [...linesToKeep, ...itemsWithCorrectLineNos];

    setLines(finalLines.length > 0 ? finalLines : [initialLine]);

    // 5. Fetch Data (Stock/DPR) ONLY for the newly added items
    if (trulyNewItems.length > 0) {
      if (header.VendorCode && header.ToFeederId) {
        fetchPendingDprBulk(header.ProjectHeaderId, header.ToFeederId, header.VendorCode, trulyNewItems);
      }
      if (header.FromFeederId && header.ProjectNo) {
        setTimeout(() => fetchStockDataFromBC(header.FromFeederId, trulyNewItems), 100);
      }
    }

    showToast(`Updated lines. Added ${trulyNewItems.length} new items.`, 'success', 'Success');
    setShowSelectionPopup(false);
  };
  
  const handlePopupKeyDown = (e) => {
    const filteredItems = getFilteredPopupItems();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPopupHighlightedIndex(prev => (prev + 1) % filteredItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPopupHighlightedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (popupHighlightedIndex >= 0 && filteredItems[popupHighlightedIndex]) {
        const item = filteredItems[popupHighlightedIndex];
        togglePopupSelection(item.SurveyBOQLineID);
      }
    }
  };

  const togglePopupSelection = (id) => {
    setSelectedPopupIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAllPopup = () => {
    const filteredItems = getFilteredPopupItems();
    const allIds = new Set(filteredItems.map(i => i.SurveyBOQLineID));
    
    // If all currently visible are selected, deselect them. Else select all visible.
    const allVisibleSelected = filteredItems.every(i => selectedPopupIds.has(i.SurveyBOQLineID));
    
    if (allVisibleSelected) {
        // Deselect visible
        const newSet = new Set(selectedPopupIds);
        filteredItems.forEach(i => newSet.delete(i.SurveyBOQLineID));
        setSelectedPopupIds(newSet);
    } else {
        // Select visible (merge with existing)
        setSelectedPopupIds(prev => new Set([...prev, ...allIds]));
    }
  };

  const getFilteredPopupItems = () => {
    if (!popupSearchTerm) return popupData;
    const term = popupSearchTerm.toLowerCase();
    return popupData.filter(item => 
      (item.ProductCode || '').toLowerCase().includes(term) || 
      (item.Description || '').toLowerCase().includes(term)
    );
  };

   const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!header.ProjectNo) {
      showToast('Project is required.', 'error', 'Required Field');
      return;
    }

    if (!header.VendorCode) {
      showToast('Vendor is required.', 'error', 'Required Field');
      return;
    }

    if (!header.ToFeederId) {
      showToast('To Feeder is required.', 'error', 'Required Field');
      return;
    }

    if (!header.FromFeederId) {
      showToast('From Feeder is required.', 'error', 'Required Field');
      return;
    }

    if (!header.SurveyBOQID) {
      showToast('Survey BOQ is required.', 'error', 'Required Field');
      return;
    }
    // if(!header.Date) {
    //   showToast('Date is required.', 'error', 'Required Field');
    //   return;
    // }
    if(!header.VendorCode) {
      showToast('Contractor is required.', 'error', 'Required Field');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const processedLines = lines.map(l => ({
        ...l,
        SurveyBOQLineID: l.SurveyBOQLineID ? parseInt(l.SurveyBOQLineID) : null,
        FeederId: l.FeederId ? parseInt(l.FeederId) : null,
        AvailableStock: parseFloat(l.AvailableStock) || 0,
        StockOnAvailableIndent: parseFloat(l.StockOnAvailableIndent) || 0,
        Quantity: parseFloat(l.Quantity) || 0,
        QtyToIssue: parseFloat(l.QtyToIssue) || 0,
        QuantityIssued: parseFloat(l.QuantityIssued) || 0,
        QtyToReceive: parseFloat(l.QtyToReceive) || 0,
        QuantityReceived: parseFloat(l.QuantityReceived) || 0,
        LineNumber: parseInt(l.LineNumber) || 0,
        PendingDPRQty: parseFloat(l.PendingDPRQty) || 0 
      }));

      const payload = {
        header: {
          ...header,
          SiteIndentNo: (initialData && initialData.header && initialData.header.SiteIndentID) ? header.SiteIndentNo : nextIndentNumber,
          SurveyBOQID: header.SurveyBOQID ? parseInt(header.SurveyBOQID) : null,
          ProjectHeaderId: header.ProjectHeaderId ? parseInt(header.ProjectHeaderId) : null,
          FromFeederId: header.FromFeederId ? parseInt(header.FromFeederId) : null,
          ToFeederId: header.ToFeederId ? parseInt(header.ToFeederId) : null
        },
        lines: processedLines
      };

      const sourceData = initialData && initialData.header ? initialData.header : initialData;
      const editId = sourceData ? sourceData.SiteIndentID : null;

      const isEditMode = !!editId;
      const url = isEditMode ? `${API_URL}/site-indent/${editId}` : `${API_URL}/site-indent`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        const errorData = await res.json();
        console.log("error message ", errorData);
        
        // Create a custom error that includes the full response data
        const error = new Error(errorData.message || 'Failed to save data.');
        error.errors = errorData.errors || null;
        throw error;
    }

      const result = await res.json(); // Parse response to get ID/No

      if (onFormSuccess) { 
        onFormSuccess(result, isEditMode ? 'Updated' : 'Created');
      }

      // 2. Close the form immediately
      onClose();

    }  catch (error) {
    // Check if it's a validation error with line details
    if (error.message && error.message.includes('exceeds') && error.errors) {
        // Update lines with correct RemainingQty and highlight
        const newLines = [...lines];
        const newHighlightedRows = [];
        
        error.errors.forEach(err => {
            const lineIndex = newLines.findIndex(l => l.SurveyBOQLineID === err.SurveyBOQLineID);
            if (lineIndex !== -1) {
                newLines[lineIndex].RemainingQty = err.RemainingQty;
                newHighlightedRows.push(lineIndex);
            }
        });
        
        setLines(newLines);
        setHighlightedRows(newHighlightedRows);
        
        // Build detailed error message
        const errorMessages = error.errors.map(err => 
            `Line ${err.LineNumber} (${err.ProductCode}): Requested ${err.RequestedQty}, Available ${err.RemainingQty}, Excess ${err.ExcessQty}`
        ).join('\n');
        
        showToast(errorMessages, 'error', 'Quantity Exceeded');
    } else {
        showToast(error.message || 'Error saving data. Please try again.', 'error', 'Save Failed');
    }
}finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

    const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
    setToast({ open: true, message, type, title });

    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  // 🔹 COLUMN RESIZE ADDITION
const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
  <th
    style={{ width }}
    className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
  >
    {children}
    <div
      className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
      onMouseDown={(e) => onResize(e.clientX)}
    />
  </th>
);


  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
             .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            button:focus, select:focus, input:focus, textarea:focus { outline: 2px solid #4299e1; outline-offset: 2px; }
            .uppercase-input { text-transform: uppercase; }
            .resizable-table {
            table-layout: fixed;
            min-width: max-content;
          }
            /* Chrome, Edge, Safari */
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Firefox */
            input[type=number] {
              -moz-appearance: textfield;
            }

            @keyframes toastIn {
            from {
              opacity: 0;
              transform: translate(-50%, -16px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0) scale(1);
            }
          }

          @keyframes toastProgress {
            from { width: 100%; }
            to { width: 0%; }
          }

          .animate-toastIn {
            animation: toastIn 0.25s ease-out;
          }

          .animate-toastProgress {
            animation: toastProgress 4s linear forwards;
          }
            // ADD THIS INSIDE THE STYLE TAG (after the existing CSS):
          .highlight-row {
              animation: highlightPulse 2s ease-in-out;
          }

          @keyframes highlightPulse {
              0%, 100% { background-color: rgba(254, 226, 226, 0.5); }
              50% { background-color: rgba(254, 226, 226, 1); }
          }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {(initialData && initialData.header && initialData.header.SiteIndentID) ? 'Edit Site Indent' : 'Create Site Indent'}
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Site Indent Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                   <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Site Indent No</label>
                  <input 
                    type="text" 
                    value={(initialData && initialData.header && initialData.header.SiteIndentID) ? header.SiteIndentNo : nextIndentNumber} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input text-gray-600" 
                  />
                </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Type</label>
                    <select 
                      value={header.TransferType} 
                      onChange={e => handleHeaderChange('TransferType', e.target.value)}
                      disabled={isHeaderLocked}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      <option value="Transfer to Contractor">Transfer to Contractor</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Date</label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.Date ? dayjs(header.Date) : null}
      onChange={(newValue) => {
        handleHeaderChange('Date', newValue ? newValue.format('YYYY-MM-DD') : '');
      }}
      disabled={isHeaderLocked}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full',
          required: true
        }
      }}
    />
  </LocalizationProvider>
</div>

                 

                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Project <span className='text-red-600'>*</span></label>
  <SearchableDropdown
    label="Project *"
    options={projects}
    value={header.ProjectNo}
    onChange={(val) => handleProjectChange({ target: { value: val } })}
    disabled={isHeaderLocked}
    placeholder="Select Project"
    getOptionLabel={(p) => `${p.No} - ${p.Description}`}
    getOptionValue={(p) => p.No}
    getOptionSecondaryLabel={(p) => p.No}
  />
</div>

                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">To Feeder (Target) <span className='text-red-600'>*</span></label>
  <SearchableDropdown
    label="To Feeder (Target) *"
    options={feeders.filter(f => f.ProjectNo === header.ProjectNo)}
    value={header.ToFeederId}
    onChange={(val) => handleToFeederChange({ target: { value: val } })}
    placeholder="Select Feeder"
    getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
    getOptionValue={(f) => f.FeederId}
    getOptionSecondaryLabel={(f) => f.Code}
    disabled={isHeaderLocked || !header.ProjectNo}
  />
</div>

<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Contractor / Vendor <span className='text-red-600'>*</span></label>
  <SearchableDropdown
    label="Constructor / Vendor"
    options={vendors}
    value={header.VendorCode}
    onChange={(val) => handleHeaderChange('VendorCode', val)}
    disabled={isHeaderLocked}
    placeholder="Select Contractor"
    getOptionLabel={(v) => v.vendor_name}
    getOptionValue={(v) => v.vendor_code}
    getOptionSecondaryLabel={(v) => v.vendor_code}
  />
</div>

                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Select BOQ <span className='text-red-600'>*</span></label>
  <SearchableDropdown
    label="Select BOQ"
    options={availableBOQs}
    value={header.SurveyBOQID}
    onChange={(val) => handleBOQSelection({ target: { value: val } })}
    placeholder="Select BOQ (Auto-fill Lines)"
    getOptionLabel={(b) => b.SurveyBOQNo}
    getOptionValue={(b) => b.SurveyBOQID}
    getOptionSecondaryLabel={(b) => b.SurveyBOQNo}
    disabled={isHeaderLocked || !header.ProjectNo || !header.ToFeederId}
  />
</div>

{/* --- INSERT THIS BLOCK INSIDE THE HEADER GRID --- */}
{/* --- ACTIVITY DROPDOWN --- */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Activity</label>
  <SearchableDropdown
    label="Activity"
    // UPDATE THIS LINE: Only show items where IsParentActivity is true
    options={projectActivities.filter(a => a.IsParentActivity === true)} 
    value={header.ActivityId}
    onChange={(val) => {
        handleHeaderChange('ActivityId', val);
        handleHeaderChange('SubActivityId', null); // Reset child when parent changes
    }}
    disabled={isHeaderLocked || !header.ProjectNo}
    placeholder="Select Activity"
    getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
    getOptionValue={(a) => a.ActivityId}
    getOptionSecondaryLabel={(a) => a.Code}
  />
</div>

{/* --- SUB-ACTIVITY DROPDOWN --- */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Sub Activity</label>
  <SearchableDropdown
    label="Sub Activity"
    options={subActivities} // This state is automatically filtered by the useEffect above
    value={header.SubActivityId}
    onChange={(val) => handleHeaderChange('SubActivityId', val)}
    disabled={isHeaderLocked || !header.ActivityId}
    placeholder="Select Sub Activity"
    getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
    getOptionValue={(a) => a.ActivityId}
    getOptionSecondaryLabel={(a) => a.Code}
  />
</div>

                 {/* Status - EXISTING FIELD */}
                  {/* <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <select value={header.Status} onChange={e => handleHeaderChange('Status', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="Open">Open</option>
                      <option value="Released">Released</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div> */}

                  {/* Approval/BC Status - ADD THIS NEW FIELD */}
              {/* Approval/BC Status */}
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-[#64748b]"> Status</label>
              <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                {!initialData ? (
                  <span className="text-xs font-medium text-gray-600 italic">Open</span>
                ) : header.ApprovalStatus === 'PENDING' ? (
                  <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                    Pending Approval (Level {header.CurrentLevel || 1})
                  </span>
                ) : header.ApprovalStatus === 'REJECTED' ? (
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                    Rejected
                  </span>
                ) : header.ApprovalStatus === 'APPROVED' ? (
                  // FIX: Check for both SendToBC and SendToBc
                  (header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                      Sent to BC Successfully
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                      Approved (Ready to Send BC)
                    </span>
                  )
                ) : (
                  <span className="text-xs text-gray-500 italic">
                    {(header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? 'Sent to BC Successfully' : 'Open'}
                  </span>
                )}
              </div>
            </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Material Status</label>
                    <select 
                      value={header.MaterialStatus} 
                      onChange={e => handleHeaderChange('MaterialStatus', e.target.value)} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] pointer-events-none" readonly
                    >
                      <option value="Pending">Pending</option>
                      <option value="Partial">Partial</option>
                      <option value="Received">Received</option>
                      <option value="Not Received">Not Received</option>
                    </select>
                  </div>

                 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    From Feeder <span className='text-red-600'>*</span>
    {isLoadingStockData && <span className="ml-2 text-blue-500 text-xs">(Loading stock data...)</span>}
  </label>
  <SearchableDropdown
    label="From Feeder"
    options={feeders.filter(f => f.ProjectNo === header.ProjectNo && f.FeederId != header.ToFeederId)}
    value={header.FromFeederId}
    onChange={(val) => {
      handleHeaderChange('FromFeederId', val);
      if (val && header.ProjectNo && lines.length > 0 && lines[0].ProductCode) {
        setTimeout(() => fetchStockDataFromBC(val), 100);
      }
    }}
    disabled={isHeaderLocked}
    placeholder="Select Source Feeder"
    getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
    getOptionValue={(f) => f.FeederId}
    getOptionSecondaryLabel={(f) => f.Code}
  />
</div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input type="text" value={header.Circle} onChange={e => handleHeaderChange('Circle', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <input type="text" value={header.District} onChange={e => handleHeaderChange('District', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <input type="text" value={header.Division} onChange={e => handleHeaderChange('Division', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <input type="text" value={header.SubDivision} onChange={e => handleHeaderChange('SubDivision', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input" />
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <input type="text" value={header.SubStation} onChange={e => handleHeaderChange('SubStation', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea value={header.Remarks} onChange={e => handleHeaderChange('Remarks', e.target.value)} disabled={isHeaderLocked} rows="2" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"></textarea>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Indent Lines</h3>
                  <div className="flex gap-2">
                     <button 
                      type="button" 
                      onClick={handleGetFilteredLines}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                      Get Data
                    </button>
                      {highlightedRows.length > 0 && (
                          <button
                              type="button"
                              onClick={() => setHighlightedRows([])}
                              className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
                          >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Clear Highlights ({highlightedRows.length} rows)
                          </button>
                      )}
                      {/* <button type="button" className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" onClick={addLine}>
                        <PlusIcon className="mr-1" /> Add Item
                      </button> */}
                  </div>
              </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
  <tr className="bg-[#BDDDFC] text-[#384959]">
    <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">
      Line No
    </ResizableTh>

    <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>
      Product Code
    </ResizableTh>

    <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>
      Task No
    </ResizableTh>

    <ResizableTh width={columnWidths.feeder} onResize={(x) => startResize('feeder', x)}>
      Feeder
    </ResizableTh>

    <ResizableTh width={columnWidths.productName} onResize={(x) => startResize('productName', x)}>
      Product Name
    </ResizableTh>

     <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>
      UOM
    </ResizableTh>
    <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
      Activity
    </ResizableTh>
    <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
      Sub Activity
    </ResizableTh>

    <ResizableTh width={columnWidths.availStock} onResize={(x) => startResize('availStock', x)} align="right">
      Avail Stock
    </ResizableTh>

    {/* <ResizableTh width={columnWidths.stockIndent} onResize={(x) => startResize('stockIndent', x)} align="right">
      Stock On Indent
    </ResizableTh> */}

    <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">
      Survey Qty
    </ResizableTh>
    <ResizableTh width={120} onResize={(x) => startResize('pendingDpr', x)} align="right">
  Pending DPR Qty
</ResizableTh>

    <ResizableTh width={columnWidths.toIssue} onResize={(x) => startResize('toIssue', x)} align="right">
      Qty
    </ResizableTh>

    <ResizableTh width={columnWidths.issued} onResize={(x) => startResize('issued', x)} align="right">
      Issued Qty
    </ResizableTh>

    {/* <ResizableTh width={columnWidths.qtyToRecv} onResize={(x) => startResize('qtyToRecv', x)} align="right">
      Qty To Recv
    </ResizableTh> */}

    <ResizableTh width={columnWidths.qtyRecv} onResize={(x) => startResize('qtyRecv', x)} align="right">
      Received Qty
    </ResizableTh>

    <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
  Remark
</ResizableTh>

   

    {/* <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center">
      Action
    </ResizableTh> */}
  </tr>
</thead>

                    <tbody>
                      {lines.map((line, idx) => {
                        const isLineLocked = line.BcSuccess === true || line.BcSuccess === 1;

                        const selectedActivityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                        const activityDisplay = selectedActivityObj 
                          ? `${selectedActivityObj.Code} - ${selectedActivityObj.Description}` 
                          : '';

                        const selectedSubActivityObj = projectActivities.find(a => a.ActivityId == line.SubActivityId);
                        const subActivityDisplay = selectedSubActivityObj 
                          ? `${selectedSubActivityObj.Code} - ${selectedSubActivityObj.Description}` 
                          : '';

                        return(
                            <tr 
                              key={idx} 
                              className={`border-t border-[#E2E8F0] transition-colors duration-300 ${
                                  highlightedRows.includes(idx) 
                                      ? 'bg-red-100 border-l-4 border-l-red-500' 
                                      : ''
                              }`}
                          >
                          <td className="p-2 text-center "  style={{ width: columnWidths.lineNo }}>
                            <input 
                              type="number" 
                              value={line.LineNumber} 
                              onChange={e => handleLineChange(idx, 'LineNumber', e.target.value)} 
                              className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" 
                            />
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.productCode }}>
                            <input type="text" value={line.ProductCode} onChange={e => handleLineChange(idx, 'ProductCode', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase-input pointer-events-none" />
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.taskNo }}>
                            <input type="text" value={line.TaskNo} onChange={e => handleLineChange(idx, 'TaskNo', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" />
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.feeder }}>
                            <select 
                              value={line.FeederId} 
                              onChange={e => handleLineChange(idx, 'FeederId', e.target.value)} 
                              disabled
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                              readOnly
                              
                            >
                              <option value="">Select Feeder</option>
                              {feeders.map(f => (
                                <option key={f.FeederId} value={f.FeederId}>{f.Code}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.productName }}>
                            <input type="text" value={line.Description} onChange={e => handleLineChange(idx, 'Description', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.uom }}>
                            <input type="text" value={line.UnitOfMeasure} onChange={e => handleLineChange(idx, 'UnitOfMeasure', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 uppercase-input pointer-events-none" readOnly />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.activity }}>
                              <input 
                                type="text" 
                                value={activityDisplay}
                                readOnly 
                                disabled
                                className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                              />
                            </td>
                          <td className="p-2" style={{ width: columnWidths.subActivity }}>
                              <input 
                                type="text" 
                                value={subActivityDisplay}
                                readOnly 
                                disabled
                                className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                              />
                            </td>
                          <td className="p-2 " style={{ width: columnWidths.availStock }}>
                            <input type="number"  inputMode="decimal"  step="0.000001" value={line.AvailableStock} onChange={e => handleLineChange(idx, 'AvailableStock', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" />
                          </td>
                          {/* <td className="p-2 " style={{ width: columnWidths.stockIndent }}>
                            <input type="number" step="1" value={line.StockOnAvailableIndent} onChange={e => handleLineChange(idx, 'StockOnAvailableIndent', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" />
                          </td> */}
                          <td className="p-2 " style={{ width: columnWidths.qty }}>
                            <input type="number"  inputMode="decimal"  step="0.000001" value={line.Quantity} onChange={e => handleLineChange(idx, 'Quantity', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" readOnly />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.pendingDpr || 120 }}>
                            <input 
                              type="number" 
                              value={line.PendingDPRQty || 0} 
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none bg-gray-50" 
                              readOnly 
                            />
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.toIssue }}>
                            <input type="number"
                               inputMode="decimal"  step="0.000001" value={line.QtyToIssue} onChange={e => handleLineChange(idx, 'QtyToIssue', e.target.value)} 
                              onFocus={(e) => e.target.select()} 
                              disabled={isLineLocked}
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" 
                            />
                          </td>
                          <td className="p-2 " style={{ width: columnWidths.issued }}>
                            <input type="number"  inputMode="decimal"  step="0.000001" value={line.QuantityIssued} onChange={e => handleLineChange(idx, 'QuantityIssued', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" 
                            readOnly/>
                          </td>
                          {/* <td className="p-2" style={{ width: columnWidths.toReceive }}>
                            <input type="number" step="0.01" value={line.QtyToReceive} onChange={e => handleLineChange(idx, 'QtyToReceive', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" />
                          </td> */}
                          <td className="p-2" style={{ width: columnWidths.qtyRecv }}>
                            <input type="number"  inputMode="decimal"  step="0.000001" value={line.QuantityReceived} onChange={e => handleLineChange(idx, 'QuantityReceived', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none" readOnly/>
                          </td>

                          <td className="p-2" style={{ width: columnWidths.remark }}>
                          <input 
                            type="text"
                            value={line.Remark || ''}
                            onChange={e => handleLineChange(idx, 'Remark', e.target.value)}
                            disabled={isLineLocked} // Use the existing lock logic
                            className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100" 
                          />
                        </td>
                          
                          {/* <td className="p-2 text-center " style={{ width: columnWidths.action }}>
                            <button type="button" className="text-red-500 hover:text-red-700 transition-colors" onClick={() => removeLine(idx)} disabled={lines.length === 1}>
                              <TrashIcon />
                            </button>
                          </td> */}
                        </tr>
                        );
})}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Quantity</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.Quantity) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total To Issue</span>
                      <span className="text-base font-semibold text-lg text-green-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.QtyToIssue) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>
                Cancel
              </button>
              <button 
  type="submit" 
  className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center" 
  disabled={loading || isSubmitting} 
  onClick={handleSubmit}
>
  {loading || isSubmitting ? 'Saving...' : (
    <>
      <SaveIcon className="mr-2" size={18} />
      {(initialData && initialData.header && initialData.header.SiteIndentID) ? 'Update Indent' : 'Save Indent'}
    </>
  )}
</button>
            </div>
          </div>

        </div>
      </div>

            {showSelectionPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-gray-200">
            
            {/* --- HEADER --- */}
            <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-[#384959]">Select Lines to Load</h3>
                <p className="text-sm text-gray-500 mt-1">
                  BOQ: <span className="font-medium text-gray-700">{boqDisplay}</span> | 
                  Activity: <span className="font-medium text-gray-700">{activityDisplay}</span> | 
                  Sub-Activity: <span className="font-medium text-gray-700">{subActivityDisplay}</span>
                </p>
              </div>
              <button 
                onClick={() => setShowSelectionPopup(false)} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* --- TOOLBAR (Search Only) --- */}
            <div className="px-5 py-3 border-b border-gray-200 bg-white">
              <div className="relative w-full">
                <input 
                  type="text" 
                  placeholder="Search Product Code, Description..." 
                  className="w-full pl-3 pr-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={popupSearchTerm}
                  onChange={(e) => { setPopupSearchTerm(e.target.value); setPopupHighlightedIndex(-1); }}
                  onKeyDown={handlePopupKeyDown}
                  autoFocus
                />
              </div>
            </div>

            {/* --- TABLE (Matching Main Table Style) --- */}
            <div className="flex-1 overflow-auto bg-white" ref={popupListRef}>
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-[#BDDDFC] text-[#384959] font-semibold sticky top-0 z-10">
                  <tr>
                    <th className="p-2 w-12 text-center border-r border-[#a8c8e8]">
                       {/* SELECT ALL CHECKBOX INSIDE HEADER */}
                      <input 
                        type="checkbox" 
                        checked={getFilteredPopupItems().length > 0 && getFilteredPopupItems().every(i => selectedPopupIds.has(i.SurveyBOQLineID))}
                        onChange={toggleSelectAllPopup}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="p-2 border-r border-[#a8c8e8]">Product Code</th>
                    <th className="p-2 border-r border-[#a8c8e8]">Description</th>
                    <th className="p-2 w-32 text-center border-r border-[#a8c8e8]">Task No</th>
                    <th className="p-2 w-40 border-r border-[#a8c8e8]">Activity</th>
                    <th className="p-2 w-40 border-r border-[#a8c8e8]">Sub Activity</th>
                    <th className="p-2 w-28 text-right border-r border-[#a8c8e8]">Survey Qty</th>
                    <th className="p-2 w-16 text-center">UOM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {getFilteredPopupItems().map((item, index) => {
                    const isSelected = selectedPopupIds.has(item.SurveyBOQLineID);
                    const isHighlighted = index === popupHighlightedIndex;

                    // Helper to find Activity Code
                    const actObj = projectActivities.find(a => a.ActivityId == item.ActivityId);
                    const actName = actObj ? actObj.Code : item.ActivityId;
                    
                    // Helper to find Sub Activity Code
                    const subActObj = projectActivities.find(a => a.ActivityId == item.SubActivityId);
                    const subActName = subActObj ? subActObj.Code : item.SubActivityId;

                    return (
                      <tr 
                        key={item.SurveyBOQLineID}
                        className={`cursor-pointer hover:bg-blue-50 ${isHighlighted ? 'bg-blue-50' : ''} ${isSelected ? 'bg-blue-50/80' : ''}`}
                        onClick={() => togglePopupSelection(item.SurveyBOQLineID)}
                      >
                        <td className="p-2 border-r border-[#E2E8F0] text-center">
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => togglePopupSelection(item.SurveyBOQLineID)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="p-2 border-r border-[#E2E8F0] font-mono text-gray-900">{item.ProductCode}</td>
                        <td className="p-2 border-r border-[#E2E8F0] text-gray-600">{item.Description}</td>
                        <td className="p-2 border-r border-[#E2E8F0] text-center text-gray-500">{item.TaskNo}</td>
                        <td className="p-2 border-r border-[#E2E8F0] text-gray-500">{actName}</td>
                        <td className="p-2 border-r border-[#E2E8F0] text-gray-500">{subActName}</td>
                        <td className="p-2 border-r border-[#E2E8F0] text-right text-gray-900">{item.Quantity}</td>
                        <td className="p-2 text-center text-gray-500">{item.UnitOfMeasure}</td>
                      </tr>
                    );
                  })}
                  {getFilteredPopupItems().length === 0 && (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-gray-500">
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* --- FOOTER --- */}
            <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center bg-white">
              <span className="text-sm text-gray-600">
                {selectedPopupIds.size} items selected
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowSelectionPopup(false)} 
                  className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePopupLoad}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                >
                  Load Selected Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* --- TOP CENTER TOAST --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div
            className={`
              relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
              ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
              ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
              ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
            `}
          >
            {/* Content */}
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}

                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">
                  {toast.title}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {toast.message}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
        </div>
      )}

        
    </>
  );
};

export default SiteIndentFormModal;